import { Currency } from "@/components/Currency";
import { type DeliveryService, type Order, type Service } from "@repo/api";
import { Text, Variables } from "@repo/skapa";
import { t } from "@repo/translation";
import {
  ElementsContainer,
  FlexDiv,
  InlineLabelField,
  Line,
  LocalizedDateTimeRange,
  translateServiceStatus,
  useSettings,
} from "@repo/ui";
import { hasItems } from "@repo/utils";
import { Fragment } from "react";

interface ServiceItemProps {
  item: DeliveryService | Service;
}
const ServiceItem = ({ item }: ServiceItemProps) => {
  const { kic_config } = useSettings();

  return (
    <FlexDiv justifyContent="space-between">
      <div>
        <Text textStyle="Heading.XS">{item.serviceName}</Text>
        {hasItems(item.timeWindows) &&
          item.timeWindows.map((tw, i) => (
            <Fragment
              // eslint-disable-next-line react/no-array-index-key
              key={i}
            >
              {i > 0 && <br />}
              <LocalizedDateTimeRange
                fromDate={tw.fromLocalDateTime}
                toDate={tw.toLocalDateTime}
              />
            </Fragment>
          ))}
        {kic_config?.enabled && item.status && (
          <Text textStyle="Body.M">
            {t("label.status")} - {translateServiceStatus(item.status)}
          </Text>
        )}
      </div>
      <Text textStyle="Heading.XS">
        <Currency
          value={item.price?.priceInclTax}
          currency={item.price?.currencyCode}
        />
      </Text>
    </FlexDiv>
  );
};

export const ServiceList = ({ order }: { order: Order | undefined }) => {
  if (!order) {
    return null;
  }

  const serviceTotal =
    order.providedServices?.map((item) => item.price.priceInclTax) ?? [];

  const deliveryTotal = (
    order.deliveryArrangements?.map((arrangement) =>
      arrangement.deliveryServices.map(
        (delivery) => delivery.price.priceInclTax
      )
    ) ?? []
  ).flat();

  const totalAmount = [...serviceTotal, ...deliveryTotal].reduce(
    (a, b) => a + b,
    0
  );

  return (
    <div>
      {hasItems(order?.providedServices) && (
        <>
          {order?.providedServices.map((service) => (
            <ServiceItem item={service} key={service.serviceItemNo} />
          ))}
        </>
      )}
      {hasItems(order?.deliveryArrangements) && (
        <>
          {order?.deliveryArrangements.map((arrangement) =>
            arrangement.deliveryServices.map((delivery) => (
              <ServiceItem item={delivery} key={delivery.serviceId} />
            ))
          )}
        </>
      )}

      <ElementsContainer margin={`${Variables.space150} 0`}>
        <Line />
        <InlineLabelField
          bold
          label={t("label.total")}
          value={<Currency value={totalAmount} currency={order.currencyCode} />}
        />
      </ElementsContainer>
    </div>
  );
};
